import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TableSortLabel,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Clear,
  Security,
  Visibility
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { PermissionGroup, Permission } from '../api/BackofficeApi';

type SortField = 'name' | 'permissions';
type SortDirection = 'asc' | 'desc';

interface ColumnFilter {
  name: string;
  permissions: string;
}


const PermissionGroups: React.FC = () => {
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<PermissionGroup | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Form state for add/edit permission group
  const [formData, setFormData] = useState({
    name: '',
    permissions: [] as Permission[]
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [saving, setSaving] = useState(false);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Column filters state
  const [columnFilters, setColumnFilters] = useState<ColumnFilter>({
    name: '',
    permissions: ''
  });
  
  // Filter menu state
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<keyof ColumnFilter | null>(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch permission groups from API
      const groups = await authAPI.getPermissionGroups();
      setPermissionGroups(groups);
      
      // Get all unique permissions from all groups
      const allPermissions = new Set<Permission>();
      groups.forEach((group: PermissionGroup) => {
        group.permissions.forEach((permission: Permission) => {
          allPermissions.add(permission);
        });
      });
      
      // Convert to array for the available permissions list
      const availablePerms: Permission[] = Array.from(allPermissions);
      setAvailablePermissions(availablePerms);
      
    } catch (err: any) {
      setError('Failed to load permission groups');
      console.error('Permission groups error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Sorting function
  const sortPermissionGroups = (groups: PermissionGroup[]) => {
    return [...groups].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'permissions':
          aValue = (a.permissions || []).length;
          bValue = (b.permissions || []).length;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filtering function
  const filterPermissionGroups = (groups: PermissionGroup[]) => {
    return groups.filter(group => {
      const matchesSearch = !searchTerm || 
        group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.permissions || []).some(permission => 
          permission.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesNameFilter = !columnFilters.name || 
        group.name?.toLowerCase().includes(columnFilters.name.toLowerCase());

      const matchesPermissionsFilter = !columnFilters.permissions || 
        (group.permissions || []).some(permission => 
          permission.toLowerCase().includes(columnFilters.permissions.toLowerCase())
        );

      return matchesSearch && matchesNameFilter && matchesPermissionsFilter;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddGroup = () => {
    setSelectedGroup(null);
    setFormData({
      name: '',
      permissions: []
    });
    setFormErrors({});
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleEditGroup = (group: PermissionGroup) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      permissions: group.permissions
    });
    setFormErrors({});
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (formData.permissions.length === 0) {
      errors.permissions = 'At least one permission is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveGroup = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      
      const selectedPermissions = formData.permissions;

      const permissionGroupData: PermissionGroup = {
        name: formData.name,
        permissions: selectedPermissions
      };

      if (editMode && selectedGroup) {
        // Update existing group
        await authAPI.updatePermissionGroup(selectedGroup.name, permissionGroupData);
        setSnackbarMessage('Permission group updated successfully');
      } else {
        // Create new group
        await authAPI.createPermissionGroup(permissionGroupData);
        setSnackbarMessage('Permission group created successfully');
      }

      // Refresh the data
      await fetchData();
      setSnackbarOpen(true);
      setDialogOpen(false);
    } catch (err: any) {
      setSnackbarMessage('Failed to save permission group');
      setSnackbarOpen(true);
      console.error('Save permission group error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (group: PermissionGroup) => {
    if (!window.confirm(`Are you sure you want to delete the permission group "${group.name}"?`)) {
      return;
    }

    try {
      setSaving(true);
      await authAPI.deletePermissionGroup(group.name);
      setSnackbarMessage('Permission group deleted successfully');
      setSnackbarOpen(true);
      
      // Refresh the data
      await fetchData();
    } catch (err: any) {
      setSnackbarMessage('Failed to delete permission group');
      setSnackbarOpen(true);
      console.error('Delete permission group error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, column: keyof ColumnFilter) => {
    setFilterMenuAnchor(event.currentTarget);
    setActiveFilterColumn(column);
  };

  const handleFilterChange = (value: string) => {
    if (activeFilterColumn) {
      setColumnFilters(prev => ({
        ...prev,
        [activeFilterColumn]: value
      }));
    }
    setFilterMenuAnchor(null);
    setActiveFilterColumn(null);
  };

  const clearFilters = () => {
    setColumnFilters({
      name: '',
      permissions: ''
    });
    setSearchTerm('');
  };

  const filteredAndSortedGroups = sortPermissionGroups(filterPermissionGroups(permissionGroups));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Permission Groups
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddGroup}
          disabled={saving}
        >
          Add Permission Group
        </Button>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search permission groups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={(e) => handleFilterClick(e, 'name')}
                >
                  Name
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={(e) => handleFilterClick(e, 'permissions')}
                >
                  Permissions
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={clearFilters}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Permission Groups Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'permissions'}
                  direction={sortField === 'permissions' ? sortDirection : 'asc'}
                  onClick={() => handleSort('permissions')}
                >
                  Permissions ({filteredAndSortedGroups.reduce((acc, group) => acc + (group.permissions?.length || 0), 0)})
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedGroups.map((group) => (
              <TableRow key={group.name} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Security color="primary" />
                    <Typography variant="body2" fontWeight="medium">
                      {group.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {(group.permissions || []).map((permission, index) => (
                      <Chip
                        key={index}
                        label={permission}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleEditGroup(group)}
                    title="Edit Permission Group"
                    color="primary"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteGroup(group)}
                    title="Delete Permission Group"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Permission Group' : 'Add Permission Group'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                placeholder="e.g., Administrators"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.permissions}>
                <InputLabel>Permissions</InputLabel>
                <Select
                  multiple
                  value={formData.permissions}
                  onChange={(e) => handleFormChange('permissions', e.target.value)}
                  input={<OutlinedInput label="Permissions" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {availablePermissions.map((permission) => (
                    <MenuItem key={permission} value={permission}>
                      <Checkbox checked={formData.permissions.indexOf(permission) > -1} />
                      <ListItemText 
                        primary={permission}
                      />
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.permissions && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                    {formErrors.permissions}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveGroup} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : (editMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuItem>
          <TextField
            autoFocus
            placeholder={`Filter by ${activeFilterColumn}...`}
            value={activeFilterColumn ? columnFilters[activeFilterColumn] : ''}
            onChange={(e) => handleFilterChange(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          />
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default PermissionGroups;
